import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import QRCode from 'react-qr-code';
import Cookies from 'js-cookie';
import { cbtAPI } from '../../api/cbt';
import { Sidebar } from './dashboard_component'; // 👈 added sidebar

const CbtPassCodeVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [cbtData, setCbtData] = useState(null);
  const [testSession, setTestSession] = useState(null);
  const [testIdInput, setTestIdInput] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Sidebar state
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Get parameters from URL
  const searchParams = new URLSearchParams(location.search);
  const cbtPassCode = searchParams.get('cbt_pass_code');
  const testIdFromUrl = searchParams.get('test_id');

  // Determine the actual test_id to use (URL takes precedence)
  const effectiveTestId = testIdFromUrl || testIdInput;

  // Get user info from cookies
  const userInfo = {
    name: Cookies.get('name'),
    email: Cookies.get('email'),
    userId: Cookies.get('user_id'),
    authCode: Cookies.get('auto_login_code')
  };

  useEffect(() => {
    fetchCBTInfo();
  }, [cbtPassCode]);

  const fetchCBTInfo = async () => {
    try {
      setLoading(true);
      const response = await cbtAPI.getPassCodeInfo(cbtPassCode);
      setCbtData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load test information');
    } finally {
      setLoading(false);
    }
  };

  const startTest = async () => {
    try {
      setLoading(true);
      setIsVerifying(true);

      // Verify pass code first
      const verifyResponse = await cbtAPI.verifyPassCode(cbtPassCode);
      if (!verifyResponse.data.valid) {
        toast.error('Invalid pass code');
        return;
      }

      // Prepare payload
      const payload = { cbt_data_id : cbtPassCode };
      
      // For close_access tests, include the test_id
      if (cbtData.access_type === 'close_access') {
        if (!effectiveTestId) {
          toast.error('Test ID is required for this restricted test.');
          return;
        }
        payload.test_id = effectiveTestId;
      }

      // Start test session
      const startResponse = await cbtAPI.startTest(payload);
      const sessionData = startResponse.data;

      setTestSession(sessionData);
      
      // Navigate to test with session info
      navigate(`/participate/cbt_question?session_id=${sessionData.session_id}&cbt_data_id=${sessionData.cbt_data_id}`);
    } catch (error) {
      const status = error.response?.status;
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to start test';

      if (status === 403) {
        toast.error('Access denied: ' + (error.response?.data?.detail || 'You are not authorized for this test.'));
      } else if (status === 402) {
        toast.error('Payment required or insufficient credits. Please check your subscription.');
      } else if (status === 400) {
        toast.error(errorMsg);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
      setIsVerifying(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getAccessTypeBadge = (type) => {
    const types = {
      'open_access': { label: 'Open Access', color: 'bg-green-100 text-green-800' },
      'close_access': { label: 'Restricted Access', color: 'bg-blue-100 text-blue-800' },
      'invite_only': { label: 'Invite Only', color: 'bg-purple-100 text-purple-800' }
    };
    return types[type] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
  };

  const getStatusBadge = (status) => {
    const statuses = {
      'published': { label: 'Published', color: 'bg-green-100 text-green-800' },
      'draft': { label: 'Draft', color: 'bg-yellow-100 text-yellow-800' },
      'scheduled': { label: 'Scheduled', color: 'bg-blue-100 text-blue-800' },
      'active': { label: 'Active', color: 'bg-emerald-100 text-emerald-800' },
      'ended': { label: 'Ended', color: 'bg-red-100 text-red-800' },
      'archived': { label: 'Archived', color: 'bg-gray-100 text-gray-800' }
    };
    return statuses[status] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
  };

  if (loading && !cbtData) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
        <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
        <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900">Loading Test Information...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!cbtData) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
        <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
        <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <div className="flex items-center justify-center h-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
              <div className="text-red-500 text-6xl mb-4">❌</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Test Not Found</h3>
              <p className="text-gray-600 mb-6">The test you're looking for doesn't exist or has been removed.</p>
              <button
                onClick={() => navigate('/participate/dashboard')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const accessType = getAccessTypeBadge(cbtData.access_type || 'open_access');
  const statusBadge = getStatusBadge(cbtData.cbt_status);
  const isCloseAccess = cbtData.access_type === 'close_access';
  const isStartDisabled = loading || 
                         cbtData.cbt_status !== 'published' ||
                         (isCloseAccess && !effectiveTestId);

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Test Information Card */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">{cbtData.cbt_name}</h2>
                  <p className="text-blue-100">{cbtData.description || 'Computer Based Test'}</p>
                </div>
                <div className="flex space-x-3 mt-4 md:mt-0">
                  <span className={`px-4 py-2 rounded-xl text-sm font-semibold ${accessType.color}`}>
                    {accessType.label}
                  </span>
                  <span className={`px-4 py-2 rounded-xl text-sm font-semibold ${statusBadge.color}`}>
                    {statusBadge.label}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Test Details */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic Info */}
                    <div className="bg-blue-50 rounded-2xl p-5 border border-blue-200">
                      <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
                        <i className="fas fa-info-circle mr-2"></i>
                        Test Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Pass Code</p>
                          <p className="text-lg font-bold text-blue-900 font-mono">{cbtPassCode}</p>
                        </div>
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Duration</p>
                          <p className="text-lg font-semibold text-blue-900">
                            {formatDuration(cbtData.cbt_duration)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Attempt Limit</p>
                          <p className="text-lg font-semibold text-blue-900">
                            {cbtData.attempt_limit || 1} attempt(s)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Schedule Info */}
                    <div className="bg-green-50 rounded-2xl p-5 border border-green-200">
                      <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                        <i className="fas fa-calendar-alt mr-2"></i>
                        Schedule
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-green-600 font-medium">Start Time</p>
                          <p className="text-lg font-semibold text-green-900">
                            {cbtData.start_time 
                              ? new Date(cbtData.start_time).toLocaleString()
                              : 'Anytime'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-green-600 font-medium">End Time</p>
                          <p className="text-lg font-semibold text-green-900">
                            {cbtData.end_time 
                              ? new Date(cbtData.end_time).toLocaleString()
                              : 'No deadline'
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Settings */}
                    <div className="bg-purple-50 rounded-2xl p-5 border border-purple-200">
                      <h3 className="text-lg font-semibold text-purple-800 mb-3 flex items-center">
                        <i className="fas fa-cog mr-2"></i>
                        Test Settings
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-purple-700">Randomize Questions</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${cbtData.randomize_questions ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {cbtData.randomize_questions ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-purple-700">Allow Back Navigation</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${cbtData.allow_back_navigation ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {cbtData.allow_back_navigation ? 'Allowed' : 'Disabled'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-purple-700">Auto Submit</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${cbtData.enable_auto_submit ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {cbtData.enable_auto_submit ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Security Settings */}
                    <div className="bg-red-50 rounded-2xl p-5 border border-red-200">
                      <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
                        <i className="fas fa-shield-alt mr-2"></i>
                        Security Settings
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-red-700">Copy/Paste</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${cbtData.allow_copy_paste ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {cbtData.allow_copy_paste ? 'Allowed' : 'Disabled'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-red-700">Fullscreen Exit</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${cbtData.allow_fullscreen_exit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {cbtData.allow_fullscreen_exit ? 'Allowed' : 'Disabled'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-red-700">Webcam Required</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${cbtData.require_webcam ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                            {cbtData.require_webcam ? 'Required' : 'Not Required'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Instructions */}
                  {cbtData.instructions && (
                    <div className="bg-yellow-50 rounded-2xl p-5 border border-yellow-200">
                      <h3 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center">
                        <i className="fas fa-clipboard-list mr-2"></i>
                        Test Instructions
                      </h3>
                      <div className="prose prose-yellow max-w-none">
                        <div dangerouslySetInnerHTML={{ __html: cbtData.instructions }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* QR Code & Action Panel */}
                <div className="lg:col-span-1">
                  <div className="sticky top-6">
                    {/* QR Code */}
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <i className="fas fa-qrcode mr-2"></i>
                        Test QR Code
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-xl flex justify-center">
                        <QRCode
                          size={180}
                          value={`${window.location.origin}/participate/verification?cbt_pass_code=${cbtPassCode}`}
                          className="mx-auto"
                        />
                      </div>
                      <p className="text-sm text-gray-500 text-center mt-3">
                        Scan to share this test
                      </p>
                    </div>

                    {/* Action Panel */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-6 border border-blue-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <i className="fas fa-play-circle mr-2 text-blue-600"></i>
                        Ready to Start?
                      </h3>
                      
                      {/* User Information */}
                      <div className="bg-white rounded-xl p-4 mb-6 border border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Your Information</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Name:</span>
                            <span className="text-sm font-medium text-gray-900">{userInfo.name || 'Not Provided'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Email:</span>
                            <span className="text-sm font-medium text-gray-900">{userInfo.email || 'Not Provided'}</span>
                          </div>
                          {effectiveTestId && (
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Test ID:</span>
                              <span className="text-sm font-medium text-gray-900 font-mono">{effectiveTestId}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Timer Warning */}
                      {cbtData.cbt_duration > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                          <div className="flex items-center text-yellow-800 mb-2">
                            <i className="fas fa-clock mr-2"></i>
                            <span className="font-semibold">Time Limit</span>
                          </div>
                          <p className="text-yellow-700 text-sm">
                            You have <strong>{formatDuration(cbtData.cbt_duration)}</strong> to complete this test.
                            The timer starts when you begin and cannot be paused.
                          </p>
                        </div>
                      )}

                      {/* Start Test Button */}
                      <button
                        onClick={startTest}
                        disabled={isStartDisabled}
                        className={`w-full py-4 px-6 rounded-xl font-bold text-white shadow-lg transition-all duration-300 transform hover:scale-105 ${
                          isStartDisabled
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800'
                        }`}
                      >
                        {loading || isVerifying ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                            {isVerifying ? 'Verifying...' : 'Starting Test...'}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center text-lg">
                            <i className="fas fa-play mr-3"></i>
                            Start Test Now
                          </div>
                        )}
                      </button>

                      {/* Status Messages */}
                      {cbtData.cbt_status !== 'published' && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                          <p className="text-sm text-red-700 text-center">
                            <i className="fas fa-exclamation-triangle mr-1"></i>
                            This test is not currently available for taking.
                          </p>
                        </div>
                      )}

                      {isCloseAccess && !effectiveTestId && (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                          <p className="text-sm text-yellow-700 text-center">
                            <i className="fas fa-id-card mr-1"></i>
                            Please enter your Test ID to start.
                          </p>
                        </div>
                      )}

                      {/* Terms Agreement */}
                      <div className="mt-4 text-xs text-gray-500 text-center">
                        <p>By starting the test, you agree to abide by the test rules and honor code.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* NEW: Test ID Input for close_access without URL test_id */}
          {isCloseAccess && !testIdFromUrl && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border-2 border-blue-200">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <i className="fas fa-id-card text-blue-600 text-xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    This is a restricted test
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Please enter your Test ID provided by your instructor.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="text"
                      value={testIdInput}
                      onChange={(e) => setTestIdInput(e.target.value)}
                      placeholder="Enter your Test ID"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Your Test ID is case‑sensitive. Contact your instructor if you don't have one.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Footer Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/participate/dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Dashboard
            </button>
            
            <div className="text-sm text-gray-600">
              Need help? Contact your instructor
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { CbtPassCodeVerification };